import React, {useId} from 'react';
import {Controller} from 'react-hook-form';
import {
    FormError,
    FormLabel, getErrorLevel,
    getInputWrapperClass,
    getWholeDivWrapperClass,
} from "./sharedStyles.tsx";
import {FormComponentProps, RadioComponent, ValidationRuleLevel} from "../DynamicFormTypes.ts";
import {useFormMethods} from "../DyanmicFormContext.tsx";
import {useFieldBlurHandler} from "../hooks/OnFieldBlurHanlder.tsx";
import {useValidationRules} from "../hooks/useValidationRules.tsx";

const RadioRenderer: React.FC<FormComponentProps<RadioComponent>> = ({ component }) => {
    const { formMethods } = useFormMethods()

    const validate = useValidationRules({ component });
    const { handleFieldBlur } = useFieldBlurHandler({
        component
    });

    const instanceId = useId();

    const radioSize = component.compactUI ? "h-3 w-3" : "h-4 w-4";
    const labelClasses = component.compactUI ? 'inline-flex items-center rounded-sm p-1' : 'flex items-center rounded-md p-2';
    const labelText = component.compactUI ? "inline-flex text-xs" : "block text-sm";
    const gapSize = component.compactUI ? "gap-1.5" : "gap-3";
    const warningColor = ' border-yellow-500 focus:border-yellow-500 focus:ring-yellow-500 text-yellow-900';
    const errorColor = ' border-red-300 focus:border-red-500 focus:ring-red-500 text-red-900';

    return (
        <Controller
            name={component.fieldKey}
            control={formMethods.control}
            rules={validate}
            render={({ field, fieldState }) => (
                <div className={getWholeDivWrapperClass(component)}>
                    <FormLabel component={component} />

                    <div className={getInputWrapperClass(component)}>
                        <div className={`grid grid-cols-1 md:grid-cols-2 ${gapSize}`}>
                            {component.options.map((option, index) => (
                                <div
                                    key={`${component.fieldKey}-option-${index}`}
                                    className={`
                                       ${labelClasses} cursor-pointer border
                                        ${fieldState.error ? (getErrorLevel(fieldState.error) === ValidationRuleLevel.WARNING ?
                                          warningColor  : errorColor): 'border-gray-300'}`}
                                    onClick={() => {
                                        field.onChange(option.value);
                                        field.onBlur();
                                    }}
                                >
                                    <input
                                        id={`${component.fieldKey}-option-${index}`}
                                        type="radio"
                                        value={option.value}
                                        checked={field.value === option.value}
                                        onChange={() => {
                                            field.onChange(option.value);
                                        }}
                                        onBlur={(_e) => {
                                            field.onBlur();
                                            handleFieldBlur();
                                        }}
                                        name={`${field.name}-${instanceId}`}
                                        className={`
                                            ${radioSize} rounded-full
                                            ${fieldState.error ? (getErrorLevel(fieldState.error) === ValidationRuleLevel.WARNING ?
                                            warningColor : errorColor) : ' border-gray-300 text-indigo-600 focus:ring-indigo-500 focus:ring-offset-0'}
                                            `}
                                    />
                                    <label
                                        htmlFor={`${component.fieldKey}-option-${index}`}
                                        className={`ml-1 ${labelText} font-medium
                                            ${fieldState.error ? (getErrorLevel(fieldState.error) === ValidationRuleLevel.WARNING ?
                                                warningColor : errorColor) : ''}`}
                                    >
                                        {option.label}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>

                    <FormError fieldState={fieldState} component={component} />
                </div>
            )}
        />
    );
}

export const Radio = React.memo(RadioRenderer);